import React from "react"
import { startGame, endGame, updateRoomSettings } from "./ballparkRoom"
import { PACKS } from "../../../data/ballparkQuestions"
import { Screen, BpButton, Pill, PlayerDot, C, F } from "./BallparkUI"
import { playSound } from "../../../utils/ballparkSounds"

const LobbyScreen = ({ room, code, isHost }) => {
  const totalRounds = room.totalRounds || 7
  const packIds = room.packIds || ["general"]

  const players = Object.entries(room.players).sort(
    (a, b) => a[1].joinedAt - b[1].joinedAt
  )

  const togglePack = async (id) => {
    if (!isHost) return
    playSound("click")
    const prev = room.packIds || ["general"]
    const next = prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    const nextPackIds = next.length ? next : prev // never allow zero packs
    await updateRoomSettings(code, { packIds: nextPackIds })
  }

  const selectAllPacks = async () => {
    if (!isHost) return
    playSound("click")
    const allIds = PACKS.map((p) => p.id)
    await updateRoomSettings(code, { packIds: allIds })
  }

  const clearAllPacks = async () => {
    if (!isHost) return
    playSound("click")
    await updateRoomSettings(code, { packIds: ["general"] })
  }

  return (
    <Screen style={{ gap: 18 }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: 2, color: C.muted }}>
          ROOM CODE
        </div>
        <div style={{ fontFamily: F.display, fontWeight: 800, fontSize: 48, letterSpacing: 2 }}>
          {code}
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.muted }}>
          Friends join at ryandymock.com/ballpark
        </div>
      </div>

      <div>
        <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1.5, color: C.muted }}>
          PLAYERS · {players.length}
        </div>
        <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
          {players.map(([pid, p]) => (
            <div
              key={pid}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                background: C.card,
                border: `1.5px solid ${C.border}`,
                borderRadius: 12,
                padding: "10px 14px",
              }}
            >
              <PlayerDot color={p.color} />
              <span style={{ fontWeight: 800, fontSize: 15 }}>{p.name}</span>
              {pid === room.hostId && <Pill style={{ marginLeft: "auto" }}>HOST</Pill>}
            </div>
          ))}
        </div>
      </div>

      <div>
        <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1.5, color: C.muted }}>
          ROUNDS
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          {[5, 7, 10].map((n) => (
            <BpButton
              key={n}
              variant={totalRounds === n ? "rust" : "panel"}
              onClick={() => {
                if (isHost) {
                  playSound("click")
                  updateRoomSettings(code, { totalRounds: n })
                }
              }}
              disabled={!isHost}
              style={{
                height: 48,
                flex: 1,
                cursor: isHost ? "pointer" : "default",
                opacity: !isHost ? (totalRounds === n ? 1 : 0.45) : 1,
              }}
            >
              {n}
            </BpButton>
          ))}
        </div>
      </div>

      <div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1.5, color: C.muted }}>
            QUESTION PACKS
          </div>
          {isHost && (
            <div style={{ display: "flex", gap: 12 }}>
              <button
                onClick={selectAllPacks}
                style={{
                  background: "none",
                  border: "none",
                  color: C.rust,
                  fontFamily: F.display,
                  fontWeight: 800,
                  fontSize: 12,
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                Select All
              </button>
              <span style={{ color: C.borderDark, fontSize: 12 }}>|</span>
              <button
                onClick={clearAllPacks}
                style={{
                  background: "none",
                  border: "none",
                  color: C.rust,
                  fontFamily: F.display,
                  fontWeight: 800,
                  fontSize: 12,
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                Clear All
              </button>
            </div>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
          {PACKS.map((pack) => {
            const on = packIds.includes(pack.id)
            return (
              <div
                key={pack.id}
                onClick={() => isHost && togglePack(pack.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  background: on ? C.highlight : C.card,
                  border: on ? `2.5px solid ${C.gold}` : `1.5px solid ${C.border}`,
                  borderRadius: 12,
                  padding: "10px 14px",
                  cursor: isHost ? "pointer" : "default",
                  opacity: !isHost && !on ? 0.6 : 1,
                  transition: "all 0.2s ease",
                }}
              >
                <div>
                  <div style={{ fontWeight: 800, fontSize: 15 }}>{pack.name}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.muted }}>
                    {pack.blurb} · {pack.questions.length} questions
                  </div>
                </div>
                <span style={{ marginLeft: "auto", fontWeight: 800, color: on ? C.gold : C.border }}>
                  {on ? "✓" : "○"}
                </span>
              </div>
            )
          })}
        </div>
      </div>

      <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 10 }}>
        {isHost ? (
          <>
            <BpButton
              onClick={() => {
                playSound("click")
                startGame(code, { totalRounds, packIds })
              }}
              disabled={players.length < 2}
            >
              {players.length < 2 ? "Waiting for players…" : "Start game"}
            </BpButton>
            <BpButton variant="ghost" onClick={() => endGame(code)}>
              Cancel game
            </BpButton>
          </>
        ) : (
          <div style={{ textAlign: "center", fontWeight: 700, color: C.muted }}>
            Waiting for the host to start…
          </div>
        )}
      </div>
    </Screen>
  )
}

export default LobbyScreen
